import { listCourseEnrollments } from "./enrollmentHelpers"
import { listOrgCertificates } from "./certHelpers"
import { listOrgOrders } from "./orderHelpers"

// Learners enrolled in one specific course — thin re-export of the existing
// listCourseEnrollments (already used by the Cohorts panel) so the course
// builder's Learners tab has one obvious import.
export const getCourseLearners = async (orgId, courseId) => {
  return listCourseEnrollments(orgId, courseId)
}

// Certificates issued for one specific course. listOrgCertificates only
// filters by orgId server-side, so this filters by courseId client-side —
// same tradeoff already made elsewhere (see coursePerformanceHelpers.js).
export const getCourseCertificates = async (orgId, courseId) => {
  const { certificates, error } = await listOrgCertificates(orgId)
  if (error) return { certificates: [], error }
  return { certificates: certificates?.filter((c) => c?.courseId === courseId) || [], error: null }
}

// Single-course analytics: learner count, average completion, quiz/capstone
// performance, and revenue. Revenue is matched via order.productTitle —
// same courseId-less-orders limitation noted in coursePerformanceHelpers.js.
export const computeCourseAnalytics = async (orgId, course) => {
  const [{ enrollments }, { orders }] = await Promise.all([
    listCourseEnrollments(orgId, course?.courseId),
    listOrgOrders(orgId),
  ])

  const totalLearners = enrollments?.length || 0
  const avgCompletion = totalLearners
    ? Math.round(enrollments.reduce((sum, e) => sum + (e?.progressPercent || 0), 0) / totalLearners)
    : 0

  const quizAttempts = []
  enrollments?.forEach((e) => {
    Object.values(e?.moduleQuizResults || {})?.forEach((r) => {
      if (r?.attemptCount) quizAttempts.push(r)
    })
  })
  const avgQuizScore = quizAttempts?.length
    ? Math.round(quizAttempts.reduce((sum, r) => sum + (r?.bestScore || 0), 0) / quizAttempts.length)
    : 0

  const capstoneAttempts = enrollments?.filter((e) => e?.capstoneResult?.attemptCount) || []
  const capstonePassRate = capstoneAttempts?.length
    ? Math.round((capstoneAttempts.filter((e) => e?.capstoneResult?.passed).length / capstoneAttempts.length) * 100)
    : 0

  const revenue =
    orders
      ?.filter((o) => o?.status === "paid" && o?.productTitle === course?.title)
      ?.reduce((sum, o) => sum + (o?.amount || 0), 0) || 0

  return { totalLearners, avgCompletion, avgQuizScore, capstonePassRate, revenue, quizAttemptCount: quizAttempts?.length }
}